import { useMemo } from 'react';
import { useAuth } from '../features/auth/hooks/use-auth';
import { useBuilderMyAccess } from '../features/builder-ai/hooks/use-builder-ai';
import { useMisalignmentsAccess } from '../features/admin/misalignments/hooks/use-misalignments';
import { useProductsAccess } from '../features/admin/products/hooks/use-products';
import { usePipelineAccess } from '../features/admin/wb-pipeline';
import { roleToPlan } from '../core/constants/roles';
import { FEATURE_BOARD_BADGE_PATH, HELPDESK_BADGE_PATHS, applyMenuBadges, getMenuForUser, type MenuItem } from '../config/menu';
import { useGmsAccess } from '@/features/gms';
import { useBpmCapabilities } from '@/features/bpm/hooks/use-bpm-capabilities';
import { usePluginFeesAccess } from '@/features/plugin-fees/hooks/use-plugin-fees';
import { useEventsAccess } from '@/features/events/hooks/use-events-access';
import { useCodeOfHonorAccess } from '@/features/code-of-honor';
import { useHelpdeskAccess } from '@/features/helpdesk/hooks/use-helpdesk-access';
import { useQueueCounts } from '@/features/helpdesk/hooks/use-queue-counts';
import { useFeatureBoardCounts, useFeatureRequestAccess } from '@/features/feature-requests/hooks/use-feature-requests';
import { usePrimeBookingAccess } from '@/features/prime-booking';
import {
  canManageCosts,
  canSeeCycles,
  canSeeOverview,
  canSeePayments,
  canSeePayouts,
  canSeeReviews,
} from '@/features/plugin-fees/utils/plugin-fees-access';

/**
 * Hook to get plan-based menu structure
 * Returns menu items filtered based on the current user's plan. The Builder AI
 * group is additionally injected whenever the backend reports the user may view
 * it (owner, active builder, or pending invitee), independent of plan/role.
 */
export function useRoleBasedMenu(): MenuItem[] {
  const { user } = useAuth();
  const { data: builderAccess } = useBuilderMyAccess();
  const canAccessBuilderAI = Boolean(builderAccess?.can_view);
  // Company Owner & Builder are separate things: owners get the full Builder AI
  // group, Builders get a trimmed one (Baseshop + Invitations). `is_owner`
  // distinguishes the two.
  const isBuilderAiOwner = Boolean(builderAccess?.is_owner);
  // Data Integrity reports are gated per-user by the backend, independent of plan.
  const { data: misalignmentsAccess } = useMisalignmentsAccess();
  const canAccessMisalignments = Boolean(misalignmentsAccess?.can_view);
  // Product Management is gated per-user by the backend, independent of plan.
  const { data: productsAccess } = useProductsAccess();
  const canAccessProducts = Boolean(productsAccess?.can_view);
  // The reporting pipeline screen is gated per-user by the backend too.
  const { data: pipelineAccess } = usePipelineAccess();
  const canAccessReportingPipeline = Boolean(pipelineAccess?.can_view);
  // Contest configuration needs wbreporting:manage, which the same payload reports.
  const canManageReporting = Boolean(pipelineAccess?.can_manage);
  // Guidance authoring is its own per-user grant (gms:author), reported by gms.
  const { data: gmsAccess } = useGmsAccess();
  const canAuthorGuidance = Boolean(gmsAccess?.gms_enabled && gmsAccess?.can_author);
  // BPM Settings is for Admin and the named BPM managers (bpm_settings:manage).
  // Hidden while capabilities load, so it never flashes in and out.
  const { data: bpmCapabilities } = useBpmCapabilities();
  const canManageBpmSettings = Boolean(bpmCapabilities?.can_manage_settings);
  // Plug-in fee reviews are for the Hierarchy Assistant (plugin_fees:review).
  const { data: pluginFeesAccess } = usePluginFeesAccess();
  const canReviewPluginFees = canSeeReviews(pluginFeesAccess);
  // The same payload: billing cycles (and fee settings, the same audience:
  // canSeeFeeSettings), costs. The own statement has no menu entry (PF56).
  const canViewPluginFeeCycles = canSeeCycles(pluginFeesAccess);
  const canManagePluginFees = canManageCosts(pluginFeesAccess);
  // Payments dashboard: :manage or :review (not :payout_approve, so not the cycles flag).
  const canViewPluginFeePayments = canSeePayments(pluginFeesAccess);
  // Overview (and SEVC totals): :manage or :review. Payouts: also :payout_approve.
  const canViewPluginFeeOverview = canSeeOverview(pluginFeesAccess);
  const canViewPluginFeePayouts = canSeePayouts(pluginFeesAccess);
  // Big Event screens open through a platform-wide grant or a per-event one, so a
  // check-in-only delegate sees Big Event → Check-in whatever their role.
  const { data: eventsAccess } = useEventsAccess();
  const bigEventScreens = eventsAccess?.surfaces;
  // Code of Honor committee/admin page: code_of_honor:* grants in the access console.
  const { data: codeOfHonorAccess } = useCodeOfHonorAccess();
  const canManageCodeOfHonor = Boolean(codeOfHonorAccess?.can_review || codeOfHonorAccess?.can_manage);
  // Helpdesk Admin: helpdesk:read in the access console. Hidden while loading.
  const { data: helpdeskAccess } = useHelpdeskAccess();
  const canAccessHelpdeskAdmin = Boolean(helpdeskAccess?.can_view);
  // Team Support (helpdesk L1 queue): active SMD, or their assistant signed in as them.
  const canAccessTeamSupport = Boolean(helpdeskAccess?.team_queue);
  // "Needs action" badges on Support Queue / Team Support (polled every 60 s).
  const { data: queueCounts } = useQueueCounts();
  const staffNeedsAction = queueCounts?.staff?.needs_action ?? 0;
  const teamNeedsAction = queueCounts?.team?.needs_action ?? 0;
  // Feature requests: who may ask (can_request) and who works the board (can_manage).
  const { data: featureAccess } = useFeatureRequestAccess();
  const canRequestFeatures = Boolean(featureAccess?.can_request);
  const canManageFeatures = Boolean(featureAccess?.can_manage);
  const { data: featureCounts } = useFeatureBoardCounts(canManageFeatures);
  const featureNeedsAction = featureCounts?.needs_action ?? 0;
  // Prime booking activity trail: prime_booking:read in the access console.
  const { data: primeBookingAccess } = usePrimeBookingAccess();
  const canViewPrimeBookingActivity = Boolean(primeBookingAccess?.can_view);

  const menu = useMemo(() => {
    const primaryRole = user?.roles?.[0] || null;
    const hasPromotionAccess = Boolean(user?.hasPromotionAccess);
    if (!primaryRole)
      return getMenuForUser(
        null,
        hasPromotionAccess,
        canAccessBuilderAI,
        isBuilderAiOwner,
        canAccessMisalignments,
        canAccessProducts,
        canAccessReportingPipeline,
        canManageReporting,
        canAuthorGuidance,
        canManageBpmSettings,
        canReviewPluginFees,
        canViewPluginFeeCycles,
        canManagePluginFees,
        canViewPluginFeePayments,
        canViewPluginFeeOverview,
        canViewPluginFeePayouts,
        bigEventScreens,
        canManageCodeOfHonor,
        canAccessHelpdeskAdmin,
        canAccessTeamSupport,
        canRequestFeatures,
        canManageFeatures,
        canViewPrimeBookingActivity
      );
    const normalizedRole = primaryRole.trim().toUpperCase().replace(/[\s-]+/g, '_');
    return getMenuForUser(
      roleToPlan(normalizedRole),
      hasPromotionAccess,
      canAccessBuilderAI,
      isBuilderAiOwner,
      canAccessMisalignments,
      canAccessProducts,
      canAccessReportingPipeline,
      canManageReporting,
      canAuthorGuidance,
      canManageBpmSettings,
      canReviewPluginFees,
      canViewPluginFeeCycles,
      canManagePluginFees,
      canViewPluginFeePayments,
      canViewPluginFeeOverview,
      canViewPluginFeePayouts,
      bigEventScreens,
      canManageCodeOfHonor,
      canAccessHelpdeskAdmin,
      canAccessTeamSupport,
      canRequestFeatures,
      canManageFeatures,
      canViewPrimeBookingActivity
    );
  }, [
    user?.hasPromotionAccess,
    user?.roles,
    canAccessBuilderAI,
    isBuilderAiOwner,
    canAccessMisalignments,
    canAccessProducts,
    canAccessReportingPipeline,
    canManageReporting,
    canAuthorGuidance,
    canManageBpmSettings,
    canReviewPluginFees,
    canViewPluginFeeCycles,
    canManagePluginFees,
    canViewPluginFeePayments,
    canViewPluginFeeOverview,
    canViewPluginFeePayouts,
    bigEventScreens,
    canManageCodeOfHonor,
    canAccessHelpdeskAdmin,
    canAccessTeamSupport,
    canRequestFeatures,
    canManageFeatures,
    canViewPrimeBookingActivity,
  ]);

  // Badges are applied separately so a count change doesn't rebuild the whole menu.
  return useMemo(
    () =>
      applyMenuBadges(menu, {
        [HELPDESK_BADGE_PATHS.supportQueue]: staffNeedsAction,
        [HELPDESK_BADGE_PATHS.teamSupport]: teamNeedsAction,
        [FEATURE_BOARD_BADGE_PATH]: featureNeedsAction,
      }),
    [menu, staffNeedsAction, teamNeedsAction, featureNeedsAction]
  );
}
